import React from "react";
import Layout from "../components/layout";
import Container from "../components/layout/container";
import { CheckCircle2, Truck, CreditCard, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { trackEvent } from "../metapixel";

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-black/10 bg-white shadow-sm ${className}`}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, className = "", ...props }) => (
  <Link
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </Link>
);

const GhostButton = ({ children, className = "", ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-black/70 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 text-sm min-w-0">
    <div className="font-medium text-black/60">{label}</div>
    <div className="font-extrabold text-black/80">{value}</div>
  </div>
);

const moneyBDT = (n) => `BDT ${Number(n).toLocaleString("en-US")}`;

export default function OrderSuccessPage() {
  /**
   * 🔴 Replace these with real order data
   * Usually passed via router state or fetched by orderId
   */
  //   const order = {
  //     id: "ORD-983742",
  //     customer: {
  //       name: "Md Nadim",
  //       phone: "01XXXXXXXXX",
  //       address: "House 12, Road No 5, Mirpur, Dhaka",
  //     },
  //     delivery: {
  //       type: "Regular (48h)",
  //       method: "Pathao Courier (Inside Dhaka)",
  //     },
  //     payment: {
  //       mode: "PARTIAL_PREPAY", // FULL_PREPAY | PARTIAL_PREPAY | COD
  //       paid: 110,
  //       due: 1320,
  //     },
  //     total: 1430,
  //   };

  const location = useLocation();
  const order = location.state?.order || {}; // Fallback to empty object if no state

  trackEvent("Purchase", {
    value: order.order_total,
    currency: "BDT",
  });

  if (!order._id) {
    return (
      <Layout>
        <main className="py-10 sm:py-14 overflow-x-hidden">
          <Container>
            <div className="mx-auto max-w-2xl text-center">
              <div className="text-xl font-bold text-black/80">
                No order information found.
              </div>
              <div className="mt-2 text-sm text-black/60">
                Please return to the homepage and place an order.
              </div>
            </div>
          </Container>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="py-10 sm:py-14 overflow-x-hidden">
        <Container>
          <div className="mx-auto max-w-2xl">
            {/* Success Header */}
            <div className="text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-8 w-8 text-emerald-700" />
              </div>

              <h1 className="mt-4 text-2xl font-black text-black/85 sm:text-3xl">
                Order Placed Successfully!
              </h1>

              <p className="mt-2 text-sm font-medium text-black/55">
                Thank you for your order. We’ll contact you shortly to confirm
                delivery.
              </p>

              <div className="mt-3 text-xs font-bold text-black/60">
                Order ID:{" "}
                <span className="text-black/80">{order.merchant_order_id}</span>
              </div>
            </div>

            {/* Order Summary */}
            <Card className="mt-8 p-5 sm:p-6">
              <div className="text-sm font-extrabold text-black/80">
                Order Summary
              </div>

              <div className="mt-4 space-y-3">
                <Row label="Customer" value={order.recipient_name} />
                <Row label="Phone" value={order.recipient_phone} />
                <Row label="Address" value={order.recipient_address} />
              </div>

              <div className="my-5 h-px bg-black/10" />

              <div className="space-y-3">
                {
                  /* Map delivery_type number to string */
                  <Row
                    label="Delivery Type"
                    value={
                      order.delivery_type === 48
                        ? "Regular (48h)"
                        : order.delivery_type === 12
                        ? "Express (12h)"
                        : "Unknown"
                    }
                  />
                }
                <Row label="Delivery Method" value={"Pathao Courier"} />
              </div>

              <div className="my-5 h-px bg-black/10" />

              <div className="space-y-3">
                <Row label="Order Total" value={moneyBDT(order.order_total)} />

                {order.payment_status === "Paid" && (
                  <Row
                    label="Payment Status"
                    value={`Paid (${moneyBDT(order.order_total)})`}
                  />
                )}

                {order.payment_status === "Unpaid" && (
                  <>
                    <Row label="Paid" value={moneyBDT(order.amount_paid)} />
                    <Row
                      label="Pay on Delivery"
                      value={moneyBDT(order.amount_to_collect)}
                    />
                  </>
                )}

                {/* {order.payment.mode === "COD" && (
                  <Row
                    label="Payment Method"
                    value={`Cash on Delivery (${moneyBDT(order.total)})`}
                  />
                )} */}
              </div>
            </Card>

            {/* Next Steps */}
            <Card className="mt-6 p-5 sm:p-6 bg-black/[0.02]">
              <div className="flex items-center gap-2 text-sm font-extrabold text-black/80">
                <Truck className="h-5 w-5 text-black/60" />
                What happens next?
              </div>

              <ul className="mt-3 space-y-2 text-sm font-medium text-black/60">
                <li>📞 Our team will call you to confirm the order</li>
                <li>📦 Your order will be dispatched shortly</li>
                <li>🚚 Delivery timeline depends on selected method</li>
              </ul>
            </Card>

            {/* Actions */}
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton to="/products" className="w-full sm:w-auto">
                <Home className="h-4 w-4" />
                Continue Shopping
              </PrimaryButton>
            </div>
          </div>
        </Container>
      </main>
    </Layout>
  );
}
